import type { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { NavBack } from '@/components/nav-back.tsx';
import { LoadingSpinner } from '@/components/loading-spinner.tsx';
import { TabBar } from '@/components/tab-bar.tsx';
import { MSG } from '@/lib/constants.ts';
import { fetchWithdrawInfo, requestWithdraw } from '@/lib/withdraw.api.ts';
import { formatPrice, returnUrl } from '@/lib/utils.ts';
import { POINT_EXPIRE_YEARS, WITHDRAW_MINIMUM_POINT } from '@/data/policy.ts';
import {
  DEPOSIT_LABEL,
  DEPOSIT_NOTE,
  KEYPAD,
  MAX_INPUT_DIGITS,
  MAX_KEY_LABEL,
  NO_ACCOUNT_TEXT,
  POINT_TO_WON,
  SUBMIT_LABEL,
  transferFee,
  WITHDRAW_AMOUNT_LABEL,
  WITHDRAW_TITLE,
} from '@/data/withdraw.ts';
import { askAlert } from '@/store/dialog.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { PageProps } from '@/types/props.ts';
import type { WithdrawInfo } from '@/types/withdraw.ts';

/** 시안 `info-bar-icon` 의 인라인 SVG */
const InfoIcon: FunctionalComponent = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

/** 시안 `key-del` 의 인라인 SVG */
const BackspaceIcon: FunctionalComponent = () => (
  <svg viewBox="0 0 24 24">
    <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
    <line x1="18" y1="9" x2="12" y2="15" />
    <line x1="12" y1="9" x2="18" y2="15" />
  </svg>
);

/**
 * 포인트 출금 신청 (시안 `_withdraw.html`).
 *
 * ★ **정책값은 서버가 준 것만 쓴다.** 수수료가 정액에서 비율로 바뀐 것처럼 정책은
 *   서버에서만 바뀐다. `data/withdraw.ts` 의 상수는 응답이 오기 전 초기값이다.
 *
 * 시안은 정적 마크업이라 키패드 동작이 없다. 아래는 새로 구현했다.
 *   숫자키   뒤에 붙인다 (출금 가능액을 넘지 않도록 잘라낸다)
 *   최대금액 출금 가능액 전액
 *   ⌫      한 자리 지우기
 */
const MypageWithdraw: FunctionalComponent<PageProps> = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const hydrated = useAppSelector((state) => state.auth.hydrated);

  const [amount, setAmount] = useState(0);
  const [info, setInfo] = useState<WithdrawInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  /** 신청 직전 확인 팝업 (새 시안) */
  const [confirmOpen, setConfirmOpen] = useState(false);

  const userSeq = Number(user?.userSeq ?? 0);

  useEffect(() => {
    if (!hydrated) return;
    if (!user?.id) {
      route(`/login?r=${encodeURIComponent(returnUrl())}`, true);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const response = await fetchWithdrawInfo(userSeq);
        if (cancelled) return;
        if (!response.result) {
          await dispatch(askAlert(response.message ?? MSG.apiFail));
          return;
        }
        setInfo(response.data ?? null);
      } catch {
        if (!cancelled) await dispatch(askAlert(MSG.apiFail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hydrated, user?.id, userSeq, dispatch]);

  const balance = info?.balance ?? user?.point ?? 0;
  const policy = info?.policy;
  const minimum = policy?.minimum ?? WITHDRAW_MINIMUM_POINT;
  const maximum = policy?.maximum ?? 0;
  const feeRate = policy?.feeRate;
  const pointToWon = policy?.pointToWon ?? POINT_TO_WON;

  /**
   * 한 번에 신청할 수 있는 금액.
   *
   * 잔액과 1회 한도 중 작은 쪽이다. 시안은 '출금 가능' 과 '보유' 를 다르게 보여주는데,
   * 그 차이가 바로 이 한도다(보유 76,120P / 출금 가능 50,000P).
   */
  const available = maximum > 0 ? Math.min(balance, maximum) : balance;

  const fee = transferFee(amount, feeRate);
  const deposit = Math.max(0, amount * pointToWon - fee);
  const account = info?.account ?? null;
  const pending = info?.pending ?? null;
  const canSubmit = amount >= minimum && amount <= available && !!account && !pending && !busy;

  /**
   * 입금 예정일.
   *
   * 신청 전이라 서버에 물어볼 건이 없다. 서버와 같은 규칙(신청한 달의 말일)으로
   * 화면에서 계산한다. 신청이 끝난 뒤에는 서버가 준 `expectDate` 를 쓴다.
   */
  const expectDate = (() => {
    const now = new Date();
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${last.getFullYear()}-${pad(last.getMonth() + 1)}-${pad(last.getDate())}`;
  })();

  function press(key: string) {
    if (key === 'del') {
      setAmount((prev) => Math.floor(prev / 10));
      return;
    }
    if (key === 'max') {
      setAmount(available);
      return;
    }

    setAmount((prev) => {
      const next = Number(`${prev}${key}`);
      if (String(next).length > MAX_INPUT_DIGITS) return prev;
      return Math.min(next, available);
    });
  }

  /** 신청 버튼 — 바로 보내지 않고 확인 팝업을 먼저 띄운다 */
  function askConfirm() {
    if (!account) {
      void dispatch(askAlert('출금 계좌를 먼저 등록해 주세요.'));
      route('/mypage/account');
      return;
    }
    if (pending) {
      void dispatch(askAlert('이미 진행중인 신청이 있습니다.'));
      return;
    }
    if (!canSubmit) return;
    setConfirmOpen(true);
  }

  async function submit() {
    if (busy) return;

    setBusy(true);
    try {
      const response = await requestWithdraw(userSeq, amount);
      if (!response.result) {
        await dispatch(askAlert(response.message ?? MSG.apiFail));
        return;
      }

      setConfirmOpen(false);
      setAmount(0);

      // 신청하면 포인트가 바로 차감된다. 화면도 서버가 준 값으로 다시 맞춘다.
      const refreshed = await fetchWithdrawInfo(userSeq);
      if (refreshed.result) setInfo(refreshed.data ?? null);

      // 마이페이지가 완료 알림을 한 번 띄우게 표시를 남긴다(시안 success-alert).
      try {
        sessionStorage.setItem('withdraw.requested', '1');
      } catch {
        // 사생활 보호 모드 등. 알림만 못 뜰 뿐 신청은 끝났다.
      }
      route('/mypage', true);
    } catch {
      await dispatch(askAlert(MSG.apiFail));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingSpinner show />;

  return (
    <div class="page page--withdraw">
      <header class="title">
        <NavBack onBack={() => route('/mypage')} />
        <span class="nav-title">{WITHDRAW_TITLE}</span>
      </header>

      <div class="section">
        <div class="withdraw-body">
          <div class="withdraw-amount-wrap">
            <button class="account-row" type="button" onClick={() => route('/mypage/account')}>
              <span class="account-text">{account ? account.label : NO_ACCOUNT_TEXT}</span>
              <span class="account-chevron">
                <img src="/images/arrow-right.png" alt="" />
              </span>
            </button>

            <div class="withdraw-header">
              <span class="withdraw-label">{WITHDRAW_AMOUNT_LABEL}</span>
              <span class="withdraw-amount">{formatPrice(amount)}P</span>
            </div>
          </div>

          <div class="info-bar">
            <span class="info-bar-text">
              출금 가능 <strong>{formatPrice(available)}P</strong> &nbsp;/&nbsp; 보유{' '}
              <strong>{formatPrice(balance)}P</strong>
            </span>
            <span
              class="info-bar-icon"
              onClick={() =>
                void dispatch(
                  askAlert(
                    `최소 ${formatPrice(minimum)}P~ 최대 ${formatPrice(maximum)}P까지 ` +
                      `출금 가능하며, 유효기간은 ${POINT_EXPIRE_YEARS}년입니다.`
                  )
                )
              }
            >
              <InfoIcon />
            </span>
          </div>
        </div>

        {/* 바텀시트 — 시안은 항상 열린 상태(`open`)다 */}
        <div class="keypad-sheet open">
          <div class="sheet-handle" />

          <div class="sheet-deposit">
            <span class="sheet-deposit-label">{DEPOSIT_LABEL}</span>
            <div class="sheet-deposit-right">
              <span class="sheet-deposit-amount">{formatPrice(deposit)}원</span>
              <span class="sheet-deposit-note">{DEPOSIT_NOTE}</span>
            </div>
          </div>

          <div class="sheet-btn-wrap">
            <button
              class="sheet-withdraw-btn"
              type="button"
              disabled={!canSubmit}
              onClick={askConfirm}
            >
              {SUBMIT_LABEL}
            </button>
          </div>

          <div class="keypad">
            {KEYPAD.map((key) => (
              <button
                key={key}
                class={
                  key === 'max' ? 'key-btn key-max' : key === 'del' ? 'key-btn key-del' : 'key-btn'
                }
                type="button"
                aria-label={key === 'del' ? '한 자리 지우기' : undefined}
                onClick={() => press(key)}
              >
                {key === 'max' ? MAX_KEY_LABEL : key === 'del' ? <BackspaceIcon /> : key}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 출금 신청 확인 (새 시안) */}
      <div class={confirmOpen ? 'popup-overlay show' : 'popup-overlay'}>
        <div class="popup-sheet">
          <div class="popup-body popup-body--pending">
            <h3 class="popup-title">
              아래와 같이
              <br />
              출금 신청됩니다.
            </h3>

            <div class="popup-detail-rows">
              <div class="popup-detail-row">
                <span class="popup-detail-label">입금 계좌</span>
                <span class="popup-detail-value">{account?.label ?? '-'}</span>
              </div>
              <div class="popup-detail-row">
                <span class="popup-detail-label">입금 예정액</span>
                <span class="popup-detail-value">{formatPrice(deposit)}원</span>
              </div>
              <div class="popup-detail-row">
                <span class="popup-detail-label">입금 예정일</span>
                <span class="popup-detail-value">{expectDate} 이내</span>
              </div>
            </div>
          </div>

          <div class="popup-footer-row">
            <button
              type="button"
              class="popup-cancel-btn"
              disabled={busy}
              onClick={() => setConfirmOpen(false)}
            >
              취소
            </button>
            <button type="button" class="popup-confirm-btn" disabled={busy} onClick={submit}>
              확인
            </button>
          </div>
        </div>
      </div>

      <TabBar path="/mypage" />
    </div>
  );
};

export default MypageWithdraw;
